import { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Switch,
  TextField
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTriangleExclamation, faXmark } from '@fortawesome/free-solid-svg-icons'
import LoadingIndicator from '../../common/LoadingIndicator'
import useIsMobile from '../../hooks/useIsMobile'
import discountsRequest from './api'
import {
  DEFAULT_MAX_SEATS,
  discountCode,
  formErrors,
  formForDuplicate,
  formFromDiscount,
  hasAdvancedConfig,
  localDateInput,
  mapsUrl,
  parseCoordinates,
  payloadFromForm
} from './utils'

const MUTED = 'text-gray-500 dark:text-gray-400'
const GENERIC_ERROR = 'No se pudo guardar. Probá de nuevo.'

const Section = ({ title, description, children }) => (
  <section className='py-5 border-t border-gray-100 dark:border-dk-border first:border-t-0 first:pt-1'>
    <h3 className='text-sm font-semibold text-gray-800 dark:text-gray-100'>{title}</h3>
    {description && <p className={`text-xs mt-1 ${MUTED}`}>{description}</p>}
    <div className='mt-3 flex flex-col gap-3'>{children}</div>
  </section>
)

const ToggleField = ({ label, description, checked, onChange }) => (
  <label className='flex items-start justify-between gap-4 cursor-pointer'>
    <span className='min-w-0'>
      <span className='block text-sm text-gray-800 dark:text-gray-100'>{label}</span>
      {description && <span className={`block text-xs mt-0.5 ${MUTED}`}>{description}</span>}
    </span>
    <Switch
      checked={checked}
      onChange={event => onChange(event.target.checked)}
      inputProps={{ 'aria-label': label }}
      sx={{ mt: -0.75, flexShrink: 0 }}
    />
  </label>
)

const initialForm = (discount, source) =>
  source ? formForDuplicate(source) : formFromDiscount(discount)

export default function DiscountDialog({ discount, source, takenCodes, onClose, onSaved }) {
  const creating = !discount
  const isMobile = useIsMobile()
  const [form, setForm] = useState(() => initialForm(discount, source))
  const [showErrors, setShowErrors] = useState(false)
  const [saving, setSaving] = useState(false)
  const [serverErrors, setServerErrors] = useState([])

  useEffect(() => {
    setForm(initialForm(discount, source))
    setShowErrors(false)
    setServerErrors([])
  }, [discount, source])

  const draft = useMemo(
    () => (creating ? { ...form, code: discountCode(form, { takenCodes }) } : form),
    [form, creating, takenCodes]
  )
  const errors = useMemo(() => formErrors(draft, { creating }), [draft, creating])
  const visibleErrors = showErrors ? errors : {}
  const hasErrors = Object.keys(errors).length > 0

  const update = (field, value) => {
    setServerErrors([])
    setForm(previous => ({ ...previous, [field]: value }))
  }

  const updateLocation = (index, field, value) =>
    setForm(previous => ({
      ...previous,
      locations: previous.locations.map((location, current) =>
        current === index ? { ...location, [field]: value } : location
      )
    }))

  const addLocation = () =>
    setForm(previous => ({
      ...previous,
      locations: [...previous.locations, { name: '', coordinates: '' }]
    }))

  const removeLocation = index =>
    setForm(previous => ({
      ...previous,
      locations: previous.locations.filter((_, current) => current !== index)
    }))

  const save = async () => {
    setShowErrors(true)
    setServerErrors([])
    if (hasErrors) return

    setSaving(true)
    const { ok, data } = await discountsRequest(creating ? '' : `/${discount._id}`, {
      method: creating ? 'POST' : 'PATCH',
      body: payloadFromForm(draft, { creating })
    })
    setSaving(false)

    if (ok && data?._id) {
      onSaved(data, { created: creating })
      return
    }
    setServerErrors(Array.isArray(data?.errors) && data.errors.length > 0 ? data.errors : [GENERIC_ERROR])
  }

  return (
    <Dialog
      open
      onClose={(_, reason) => {
        if (!saving && reason !== 'backdropClick') onClose()
      }}
      maxWidth='sm'
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        {source ? `Duplicar ${source.code}` : creating ? 'Nuevo descuento' : `Editar ${discount.code}`}
      </DialogTitle>
      <DialogContent>
        {source && hasAdvancedConfig(source) && (
          <p className='flex items-start gap-2 mb-2 text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 rounded-lg px-3 py-2'>
            <FontAwesomeIcon icon={faTriangleExclamation} className='mt-0.5' aria-hidden />
            La configuración avanzada de {source.code} (A/B, variantes por tipo de viaje o precio
            objetivo) no se copia.
          </p>
        )}
        {!creating && hasAdvancedConfig(discount) && (
          <p className='flex items-start gap-2 mb-2 text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 rounded-lg px-3 py-2'>
            <FontAwesomeIcon icon={faTriangleExclamation} className='mt-0.5' aria-hidden />
            Este descuento tiene configuración avanzada (A/B, variantes por tipo de viaje o precio
            objetivo) que no se edita desde acá y se mantiene como está.
          </p>
        )}

        <Section
          title='Descuento'
          description='Se descuenta del precio total que paga el pasajero, con la comisión incluida.'
        >
          <TextField
            size='small'
            label='Código'
            value={draft.code}
            disabled={!creating}
            error={!!visibleErrors.code}
            helperText={
              visibleErrors.code ||
              (creating
                ? 'Se arma solo: ruta, porcentaje y fecha de hoy. Queda guardado en cada compra y no se puede cambiar.'
                : 'No se puede cambiar: los topes se cuentan por código.')
            }
            placeholder='Completá la ruta y el porcentaje'
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size='small'
            label='Porcentaje'
            value={form.percentage}
            onChange={event => update('percentage', event.target.value)}
            disabled={!creating}
            error={!!visibleErrors.percentage}
            helperText={visibleErrors.percentage}
            inputProps={{ inputMode: 'decimal' }}
            InputProps={{ endAdornment: <InputAdornment position='end'>%</InputAdornment> }}
            sx={{ maxWidth: 200 }}
          />
          {!creating && (
            <p className={`text-xs ${MUTED}`}>
              El porcentaje no se cambia: va en el código. Para otro porcentaje, duplicá el descuento
              y apagá este.
            </p>
          )}
        </Section>

        <Section
          title='Ruta'
          description='Aplica cuando el origen y el destino caen, cada uno, cerca de dos puntos distintos. Con un solo punto aplica a cualquier viaje que salga o llegue ahí.'
        >
          {form.locations.map((location, index) => {
            const coordinates = parseCoordinates(location.coordinates)
            const error = visibleErrors[`locations.${index}`]
            const nameError = visibleErrors[`locations.${index}.name`]
            return (
              <div key={index} className='flex flex-wrap sm:flex-nowrap items-start gap-2'>
                <TextField
                  size='small'
                  label='Nombre'
                  value={location.name}
                  onChange={event => updateLocation(index, 'name', event.target.value)}
                  error={!!nameError}
                  helperText={nameError}
                  sx={{ width: { xs: '100%', sm: 170 }, flexShrink: 0 }}
                />
                <TextField
                  size='small'
                  label='Latitud, longitud'
                  placeholder='-34.6037, -58.3816'
                  value={location.coordinates}
                  onChange={event => updateLocation(index, 'coordinates', event.target.value)}
                  error={!!error}
                  helperText={
                    error ||
                    (coordinates ? (
                      <a
                        href={mapsUrl(coordinates)}
                        target='_blank'
                        rel='noreferrer'
                        className='text-primary hover:underline'
                      >
                        Ver en el mapa
                      </a>
                    ) : (
                      'Copiá las coordenadas desde Google Maps'
                    ))
                  }
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <button
                  className={`w-9 h-10 rounded-lg flex items-center justify-center hover:bg-gray-100 dark:hover:bg-dk-hover disabled:opacity-30 disabled:cursor-not-allowed ${MUTED}`}
                  onClick={() => removeLocation(index)}
                  disabled={form.locations.length === 1}
                  aria-label={`Quitar el punto ${location.name || index + 1}`}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            )
          })}
          {visibleErrors.locations && (
            <p className='text-xs text-red-600 dark:text-red-400'>{visibleErrors.locations}</p>
          )}
          <button
            className='self-start inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-primary hover:bg-primary/10 transition-colors'
            onClick={addLocation}
          >
            <FontAwesomeIcon icon={faPlus} className='text-xs' />
            Agregar punto
          </button>
          <TextField
            size='small'
            label='Radio de cada punto'
            value={form.margin}
            onChange={event => update('margin', event.target.value)}
            error={!!visibleErrors.margin}
            helperText={visibleErrors.margin}
            inputProps={{ inputMode: 'decimal' }}
            InputProps={{ endAdornment: <InputAdornment position='end'>km</InputAdornment> }}
            sx={{ maxWidth: 200 }}
          />
        </Section>

        <Section title='Quién lo recibe'>
          <ToggleField
            label='Sólo primer viaje'
            description='Cuenta también los viajes de las cuentas que comparten el dispositivo.'
            checked={form.requires_first_trip}
            onChange={value => update('requires_first_trip', value)}
          />
          <ToggleField
            label='Sólo con push de vista abandonada'
            description='Lo recibe quien abrió un viaje de esta ruta, no se unió y le llegó el push. Prenderlo también activa ese push en la ruta.'
            checked={form.requires_notified_lobby_view}
            onChange={value => update('requires_notified_lobby_view', value)}
          />
          <TextField
            size='small'
            label='Blacklist'
            placeholder='Un id de usuario por línea'
            value={form.blacklist}
            onChange={event => update('blacklist', event.target.value)}
            helperText='Estos usuarios nunca reciben el descuento.'
            multiline
            minRows={2}
            maxRows={6}
          />
        </Section>

        <Section
          title='Topes por persona'
          description={`Cuentan todas las compras con el descuento, sin reiniciarse cada mes, y suman las cuentas del mismo dispositivo. Vacío usa ${DEFAULT_MAX_SEATS} asientos y compras sin tope. Por compra se descuentan hasta 2 asientos.`}
        >
          <div className='flex flex-wrap gap-3'>
            <TextField
              size='small'
              label='Compras'
              placeholder='Sin tope'
              value={form.max_uses_per_month}
              onChange={event => update('max_uses_per_month', event.target.value)}
              error={!!visibleErrors.max_uses_per_month}
              helperText={visibleErrors.max_uses_per_month}
              inputProps={{ inputMode: 'numeric' }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 140 }}
            />
            <TextField
              size='small'
              label='Asientos'
              placeholder={String(DEFAULT_MAX_SEATS)}
              value={form.max_seats_per_month}
              onChange={event => update('max_seats_per_month', event.target.value)}
              error={!!visibleErrors.max_seats_per_month}
              helperText={visibleErrors.max_seats_per_month}
              inputProps={{ inputMode: 'numeric' }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 140 }}
            />
          </div>
          <TextField
            size='small'
            label='Comparte el tope con'
            placeholder='RCCBA50, OTRO10'
            value={form.shares_limit_with}
            onChange={event => update('shares_limit_with', event.target.value)}
            helperText={
              source
                ? 'Comparten el tope: las compras con cualquiera de los códigos cuentan para todos.'
                : 'Las compras con estos códigos también cuentan para el tope de este descuento.'
            }
          />
        </Section>

        <Section title='Cómo se muestra'>
          <ToggleField
            label='Mostrar en viajes similares'
            checked={form.show_in_similars}
            onChange={value => update('show_in_similars', value)}
          />
          <ToggleField
            label='Modo medición'
            description='Se muestra el descuento pero se cobra el precio completo.'
            checked={form.fake}
            onChange={value => update('fake', value)}
          />
        </Section>

        <Section
          title='Vencimiento'
          description='Es informativo: no apaga el descuento. La app muestra «Vence el …» hasta esa fecha.'
        >
          <div className='flex items-start gap-2'>
            <TextField
              size='small'
              type='date'
              label='Vence el'
              value={form.expires_at}
              onChange={event => update('expires_at', event.target.value)}
              error={!!visibleErrors.expires_at}
              helperText={visibleErrors.expires_at}
              inputProps={{ min: localDateInput(Date.now()) }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 200 }}
            />
            {form.expires_at && (
              <button
                className={`px-2 py-1.5 rounded-lg text-sm hover:bg-gray-100 dark:hover:bg-dk-hover ${MUTED}`}
                onClick={() => update('expires_at', '')}
              >
                Quitar fecha
              </button>
            )}
          </div>
        </Section>

        <Section title='Estado'>
          <ToggleField
            label='Activo'
            description={
              creating
                ? 'Si lo dejás apagado, lo prendés después desde la lista.'
                : 'Apagado no se aplica en ningún lado.'
            }
            checked={form.active}
            onChange={value => update('active', value)}
          />
        </Section>
      </DialogContent>
      {(serverErrors.length > 0 || (showErrors && hasErrors)) && (
        <div
          role='alert'
          className='mx-6 mt-3 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 text-sm text-red-700 dark:text-red-300'
        >
          {serverErrors.length > 0
            ? serverErrors.map(error => <p key={error}>{error}</p>)
            : 'Revisá los campos marcados.'}
        </div>
      )}
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <p className={`mr-auto text-xs ${MUTED}`}>Llega a la app en hasta 1 minuto.</p>
        <button
          className='px-3 py-2 rounded-lg bg-light_gray dark:bg-dk-raised text-black dark:text-gray-100 hover:opacity-90 disabled:opacity-50 transition-opacity'
          onClick={onClose}
          disabled={saving}
        >
          Cancelar
        </button>
        <button
          className='inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white hover:opacity-90 disabled:opacity-60 transition-opacity'
          onClick={save}
          disabled={saving}
        >
          {saving && <LoadingIndicator color='#ffffff' size={12} />}
          {saving ? 'Guardando…' : creating ? 'Crear descuento' : 'Guardar cambios'}
        </button>
      </DialogActions>
    </Dialog>
  )
}
